import {
  createAsyncThunk,
  createSlice,
  isAsyncThunkAction,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "app.store";
import { IBranchModel } from "states/branch/branch.models";
import { setLoading } from "states/global-value/global-value.slice";
import {
  addItemShoppingCartAPI,
  clearShoppingCartAPI,
  getShoppingCartAPI,
  removedItemShoppingCartAPI,
  restoreShoppingCartAPI,
  selectedBranchShoppingCartAPI,
  updateItemShoppingCartAPI,
} from "./shopping-cart.api";
import ShoppingCart, {
  AdditionalFeeItemModel,
  IAddUpdateShoppingCartItemModel,
  IProductItemSelectedModel,
  IRestoreShoppingCartModel,
  IShoppingCartState, ShoppingApplyCouponModel,
  ShoppingCartApiModel, ShoppingCartGiftItemModel,
  ShoppingCartGuestCustomerInfoModel,
  ShoppingCartItem, ShoppingCartPromotionRequestModel,
} from "./shopping-cart.models";
// First, create the thunk
export const fetchShoppingCart = createAsyncThunk(
  "shopping-cart/fetchRestoreAndGetShoppingCart",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      dispatch(setLoading(true));
      const state = getState() as RootState;
      const shoppingCart = state.shoppingCart.shoppingCart;
      if (shoppingCart.branch && shoppingCart.branch.id && !state.globalValue.isLoadingLocationParams) {
        const res = await restoreShoppingCartAPI({
          branchId: shoppingCart.branch.id,
          items:
            shoppingCart.items?.map((m) => {
              return {
                productId: m.productId,
                quantity: m.quantity,
                modifiers: m.modifiers?.map((modi) => {
                  return {
                    productId: modi.productId,
                    quantity: 1,
                  } as IAddUpdateShoppingCartItemModel;
                }),
              } as IAddUpdateShoppingCartItemModel;
            }) ?? [],
        } as IRestoreShoppingCartModel);
        dispatch(setLoading(false));
        return mapFromShoppingCartApiModel(res.data);
      }
    } catch (err) {
      dispatch(setLoading(false));
      return rejectWithValue(err.response?.data?.error?.message);
    }
  }
);

export const addItemShoppingCart = createAsyncThunk(
  "shopping-cart/addItem",
  async (
    addModel: ShoppingCartItem,
    { rejectWithValue, dispatch, getState }
  ) => {
    const state = getState() as RootState;
    if (state.globalValue.isSignIn) {
      try {
        dispatch(setLoading(true));
        const res = await addItemShoppingCartAPI(addModel);
        dispatch(setLoading(false));
        return res.data;
      } catch (err) {
        dispatch(setLoading(false));
        return rejectWithValue(err.response?.data?.error?.message);
      }
    } else {
      dispatch(shoppingCartSlice.actions.addItem(addModel));
      return null;
    }
  }
);
export const updateQuantityItemShoppingCart = createAsyncThunk(
  "shopping-cart/updateQuantityItem",
  async (
    model: { index: number; quantity: number },
    { rejectWithValue, dispatch, getState }
  ) => {
    const state = getState() as RootState;
    if (state.globalValue.isSignIn) {
      try {
        dispatch(setLoading(true));
        const res = await updateItemShoppingCartAPI({
          ...state.shoppingCart.shoppingCart.items[model.index],
          quantity: model.quantity,
        });
        dispatch(setLoading(false));
        return res.data;
      } catch (err) {
        dispatch(setLoading(false));
        return rejectWithValue(err.response?.data?.error?.message);
      }
    } else {
      dispatch(
        shoppingCartSlice.actions.updateQuantity({
          index: model.index,
          quantity: model.quantity,
        })
      );
      return null;
    }
  }
);

export const removeItemShoppingCart = createAsyncThunk(
  "shopping-cart/removedItem",
  async (index: number, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;
    if (state.globalValue.isSignIn) {
      try {
        dispatch(setLoading(true));
        const item = state.shoppingCart.shoppingCart.items[index];
        const res = await removedItemShoppingCartAPI(item);
        dispatch(setLoading(false));
        return res.data;
      } catch (err) {
        dispatch(setLoading(false));
        return rejectWithValue(err.response?.data?.error?.message);
      }
    } else {
      dispatch(shoppingCartSlice.actions.removeItem(index));
      return null;
    }
  }
);

export const selectedBranchShoppingCart = createAsyncThunk(
  "shopping-cart/selectedBranch",
  async (branch: IBranchModel, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;
    if (state.globalValue.isSignIn) {
      try {
        dispatch(setLoading(true));
        const res = await selectedBranchShoppingCartAPI(branch.id);
        dispatch(setLoading(false));
        return res.data;
      } catch (err) {
        dispatch(setLoading(false));
        return rejectWithValue(err.response?.data?.error?.message);
      }
    } else {
      dispatch(shoppingCartSlice.actions.selectBranch(branch));
      return null;
    }
  }
);

export const clearShoppingCart = createAsyncThunk(
  "shopping-cart/clearShoppingCart",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      await clearShoppingCartAPI();
      const res = await getShoppingCartAPI();
      dispatch(setLoading(false));
      return res.data;
    } catch (err) {
      dispatch(setLoading(false));
      return rejectWithValue(err.response?.data?.error?.message);
    }
  }
);

const shoppingCartLocalStorage = "ShoppingCart";

export const getShoppingCartLocalStorage = () => {
  return JSON.parse(
    localStorage.getItem(shoppingCartLocalStorage) ?? "{}"
  ) as ShoppingCartApiModel;
};

export const setShoppingCartLocalStorage = (model: ShoppingCartApiModel) => {
  return localStorage.setItem(shoppingCartLocalStorage, JSON.stringify(model));
};

export const shoppingCartSlice = createSlice({
  name: "shoppingCart",
  initialState: {
    shoppingCart: mapFromShoppingCartApiModel(getShoppingCartLocalStorage()),
    modalAddItem: false,
    viewCartModal: false,
    productSelected: {} as IProductItemSelectedModel,
    guestCustomerInfo:{} as ShoppingCartGuestCustomerInfoModel
  } as IShoppingCartState,
  reducers: {
    setModalAddItem(state, action: { payload: boolean }) {
      state.modalAddItem = action.payload;
    },
    setProductSelected(state, action: { payload: IProductItemSelectedModel }) {
      state.productSelected = action.payload;
    },
    showViewCartModal(state) {
      state.viewCartModal = true;
    },
    hideViewCartModal(state) {
      state.viewCartModal = false;
    },
    setShoppingCart(state, action: { payload: ShoppingCart }) {
      state.shoppingCart = { ...action.payload };
      updateTotal(state.shoppingCart);
    },
    setCartPromotion(state, action: { payload: ShoppingCartPromotionRequestModel }) {
      state.shoppingCart.discount = action.payload.discount;
      state.shoppingCart.cartPromotions = action.payload.cartPromotions;
      updateTotal(state.shoppingCart);
    },
    removeCoupon(state, action: { payload: number }) {
      state.shoppingCart.discount = Number(state.shoppingCart.discount) > 0 ? Number(state.shoppingCart.discount) - action.payload : Number(state.shoppingCart.discount);
      state.shoppingCart.couponCode = "";
      state.shoppingCart.productOfGifts = [];
      updateTotal(state.shoppingCart);
    },
    applyCoupon(state, action: { payload: ShoppingApplyCouponModel }) {
      state.shoppingCart.discount = action.payload.discount;
      state.shoppingCart.couponCode = action.payload.couponCode;
      state.shoppingCart.productOfGifts = [];
      updateTotal(state.shoppingCart);
    },
    addItem(state, action: PayloadAction<ShoppingCartItem>) {
      let oldItemIndex = getIndexExistedItemIndex(
        state.shoppingCart,
        action.payload
      );
      if (oldItemIndex > -1) {
        state.shoppingCart.items[oldItemIndex].quantity +=
          action.payload.quantity;
      } else {
        state.shoppingCart.items.unshift(action.payload);
      }
      updateTotal(state.shoppingCart);
      state.modalAddItem = false;
    },
    updateQuantity(
      state,
      action: PayloadAction<{ index: number; quantity: number }>
    ) {
      state.shoppingCart.items[action.payload.index].quantity =
        action.payload.quantity;
      updateTotal(state.shoppingCart);
    },
    updateGiftQuantity(
      state,
      action: PayloadAction<{ index: number; quantity: number }>
    ) {
      state.shoppingCart.productOfGifts[action.payload.index].quantity =
        action.payload.quantity;
    },
    updateGiftCartPromotionQuantity(
      state,
      action: PayloadAction<{ cartPromotionIndex: number; index: number; quantity: number }>
    ) {
      state.shoppingCart.cartPromotions[action.payload.cartPromotionIndex].cartPromotionItems[action.payload.index].quantity =
        action.payload.quantity;
    },
    addGiftCartPromotion(
      state,
      action: PayloadAction<{ cartPromotionIndex: number; items: ShoppingCartGiftItemModel[] }>
    ) {
      state.shoppingCart.cartPromotions[action.payload.cartPromotionIndex].cartPromotionItems = action.payload.items;
    },
    removeGiftCartPromotion(
      state,
      action: PayloadAction<{ cartPromotionIndex: number; index: number }>
    ) {
      state.shoppingCart.cartPromotions[action.payload.cartPromotionIndex].cartPromotionItems.splice(action.payload.index, 1);
    },
    removeItem(state, action: PayloadAction<number>) {
      state.shoppingCart.items.splice(action.payload, 1);
      updateTotal(state.shoppingCart);
    },
    setAdditionalTax(state, action: PayloadAction<AdditionalFeeItemModel[]>) {
      state.shoppingCart.additionalFeeItems = action.payload;
    },
    selectBranch(state, action: PayloadAction<IBranchModel>) {
      if (state.shoppingCart.branch.id !== action.payload.id) {
        clearCart(state.shoppingCart);
      }
      state.shoppingCart.branch = { ...action.payload };
      updateTotal(state.shoppingCart);
    },
    updateGuestCustomerInfo(state, action: { payload: ShoppingCartGuestCustomerInfoModel }) {
      state.guestCustomerInfo = action.payload;
    },
     clearShoppingCartGuest(state) {
      state.shoppingCart.items = [];
      state.shoppingCart.couponCode = undefined;
      state.shoppingCart.discount = 0;
      state.shoppingCart.tip = 0;
      state.shoppingCart.taxes = 0;
      state.shoppingCart.nonCashAdjFee = 0;
      state.shoppingCart.requestPickUpTime = undefined;
      state.shoppingCart.productOfGifts = [];
      state.shoppingCart.cartPromotions = [];
      updateTotal(state.shoppingCart);
    },
     setPickupTime(state, action: { payload: Date }) {
      state.shoppingCart.requestPickUpTime = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAsyncThunkAction(
        addItemShoppingCart,
        updateQuantityItemShoppingCart,
        removeItemShoppingCart,
        fetchShoppingCart,
        selectedBranchShoppingCart,
        clearShoppingCart
      ),
      (state, action) => {
        if (action.meta.requestStatus === "fulfilled") {
          if (action.payload) {
            const cart = action.payload as ShoppingCartApiModel;
            state.shoppingCart = updateFromShoppingCartApiModel(
              cart,
              state.shoppingCart
            );
            state.modalAddItem = false;
          }
        }
      }
    );
  },
});

export const {
  setProductSelected,
  setModalAddItem,
  setShoppingCart,
  setCartPromotion,
  applyCoupon,
  removeCoupon,
  addGiftCartPromotion,
  removeGiftCartPromotion,
  showViewCartModal,
  hideViewCartModal,
  updateGiftQuantity,
  updateGiftCartPromotionQuantity,
  setAdditionalTax,
  selectBranch,
  updateGuestCustomerInfo,
  clearShoppingCartGuest,
  setPickupTime,
} = shoppingCartSlice.actions;

export const selectShoppingCart = (state: RootState) =>
  state.shoppingCart.shoppingCart;
export const selectModalAddCart = (state: RootState) =>
  state.shoppingCart.modalAddItem;
export const selectViewCartModal = (state: RootState) =>
  state.shoppingCart.viewCartModal;
export const selectProductSelected = (state: RootState) =>
  state.shoppingCart.productSelected;

export const selectGuestCustomerInfo = (state: RootState) =>
  state.shoppingCart.guestCustomerInfo;

export function calculateTotal(item: ShoppingCartItem) {
  return (item.price + getPriceModifier(item)) * item.quantity;
}
function getPriceModifier(item: ShoppingCartItem) {
  return item.modifiers
    ?.map((m) => m.price * m.quantity)
    .reduce((a, b) => a + b, 0);
}

export function mapToShoppingCartApiModel(
  shoppingCart: ShoppingCart
): ShoppingCartApiModel {
  return {
    branch: shoppingCart.branch,
    items: shoppingCart.items.map((m) => ({
      name: m.name,
      image: m.image,
      price: m.price,
      productId: m.productId,
      quantity: m.quantity,
      modifiers: m.modifiers.map((mod) => ({
        name: mod.name,
        image: mod.image,
        price: mod.price,
        productId: mod.productId,
        quantity: mod.quantity,
      })),
    })),
  } as ShoppingCartApiModel;
}

function mapFromShoppingCartApiModel(
  model: ShoppingCartApiModel
): ShoppingCart {
  const shoppingCart = {
    branch: model.branch ?? {},
    additionalFeeItems: [],
    items: model.items
      ? model.items.map((item) => ({
        image: item.image,
        modifiers: item.modifiers.map((mod) => ({
          image: mod.image,
          name: mod.name,
          price: mod.price,
          productId: mod.productId,
          quantity: mod.quantity,
          modifiers: [],
        })),
        name: item.name,
        price: item.price,
        productId: item.productId,
        quantity: item.quantity,
      }))
      : ([] as ShoppingCartItem[]),
    discount: 0,
    subTotal: 0,
    taxes: 0,
    tip: 0,
    total: 0,
    billingAddressId: undefined,
    couponCode: undefined,
    productOfGifts: [],
    cartPromotions: [],
    nonCashAdjFee: 0,
    requestPickUpTime: undefined,
  } as ShoppingCart;
  updateTotal(shoppingCart);
  return shoppingCart;
}

function updateFromShoppingCartApiModel(
  model: ShoppingCartApiModel,
  desModel: ShoppingCart
) {
  desModel = {
    ...desModel,
    branch: model.branch ?? {},
    items: model.items
      ? model.items.map((item) => ({
        image: item.image,
        modifiers: item.modifiers.map((mod) => ({
          image: mod.image,
          name: mod.name,
          price: mod.price,
          productId: mod.productId,
          quantity: mod.quantity,
          modifiers: [],
        })),
        name: item.name,
        price: item.price,
        productId: item.productId,
        quantity: item.quantity,
      }))
      : ([] as ShoppingCartItem[]),
  };
  updateTotal(desModel);
  return desModel;
}

function getIndexExistedItemIndex(
  shoppingCart: ShoppingCart,
  item: ShoppingCartItem
) {
  return shoppingCart.items.findIndex(
    (i) =>
      i.productId === item.productId &&
      i.modifiers.length === item.modifiers.length &&
      item.modifiers.every((x) =>
        i.modifiers.some((m) => m.productId === x.productId)
      )
  );
}

function updateSubTotal(shoppingCart: ShoppingCart) {
  shoppingCart.subTotal =
    Math.round(
      (shoppingCart.items
        .map((m) => calculateTotal(m))
        .reduce((a, b) => a + b, 0) ?? 0) * 100
    ) / 100;
}

function updateTaxes(shoppingCart: ShoppingCart): void {
  shoppingCart.taxes =
    Math.round(
      (shoppingCart.subTotal - shoppingCart.discount) *
      (Number(shoppingCart.branch.taxRate ?? 0) / 100) *
      100
    ) / 100;
}

function updateNonCashAdj(shoppingCart: ShoppingCart): void {
  if (shoppingCart.branch.excludeNonCashAdjWeb) {
    shoppingCart.nonCashAdjFee = 0;
  } else {
    shoppingCart.nonCashAdjFee =
      Math.round(
        (shoppingCart.subTotal - shoppingCart.discount) *
        (Number(shoppingCart.branch.nonCashAdjRate ?? 0) / 100) *
        100
      ) / 100;
  }
}
function getTotalAdditionalFee(shoppingCart: ShoppingCart) {
  let additionalFeeItems: AdditionalFeeItemModel[] = [];
  for (let tax of shoppingCart.additionalFeeItems) {
    additionalFeeItems.push({
      taxName: tax.taxName,
      taxRate: tax.taxRate,
      feeAmount: Math.round(
        (shoppingCart.subTotal - shoppingCart.discount) *
        (Number(tax.taxRate ?? 0) / 100) *
        100
      ) / 100
    });
  }
  shoppingCart.additionalFeeItems = additionalFeeItems;
  return shoppingCart.additionalFeeItems.map(m => m.feeAmount).reduce((a, b) => a + b, 0);
}
function updateTotal(shoppingCart: ShoppingCart): void {
  updateSubTotal(shoppingCart);
  updateTaxes(shoppingCart);
  updateNonCashAdj(shoppingCart);
  const totalAdditionFee = Number(getTotalAdditionalFee(shoppingCart));
  shoppingCart.total =
    Math.round(
      (Number(shoppingCart.subTotal ?? 0) -
        Number(shoppingCart.discount ?? 0) +
        Number(shoppingCart.tip ?? 0) +
        Number(shoppingCart.taxes ?? 0) +
        Number(shoppingCart.nonCashAdjFee ?? 0) +
        totalAdditionFee
      ) *
      100
    ) / 100;
}

function clearCart(shoppingCart: ShoppingCart) {
  shoppingCart.items = [];
  shoppingCart.couponCode = undefined;
  shoppingCart.branch = {} as IBranchModel;
  shoppingCart.discount = 0;
  shoppingCart.tip = 0;
  shoppingCart.taxes = 0;
  shoppingCart.nonCashAdjFee = 0;
  shoppingCart.requestPickUpTime = undefined;
  shoppingCart.productOfGifts = [];
  shoppingCart.cartPromotions = [];
  updateTotal(shoppingCart);
}
